/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles, SubmitHandler } from '@unform/core';
import { Form } from '@unform/web';
import { format, addDays } from 'date-fns';
import { IoIosArrowBack } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Input, InputDate, InputMask, Layout, Textarea } from '~/components';
import { useAuth } from '~/hooks/auth';
import { useStudent } from '~/hooks/student';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import {
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  PageHeaderSaveButton,
  Container,
  FormContainer,
  FieldSet,
  FormLabel,
  ImageLabel,
  Image,
  FieldsWrapper,
  RadioInput,
} from './styles';

type IParams = {
  userId: string;
};

type FormProps = {
  image_url: string;
  name: string;
  email: string;
  cellphone: string;
  address_zip_code: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_city: string;
  address_state: string;
  address_country: string;
  address_neighborhood: string;
  cpf: string;
  genre?: string;
  birth_date: string;
  date_registration: string;
  observations: string;
};

const StudentRegister = () => {
  const { goBack } = useHistory();
  const { userId: studentId } = useParams<IParams>();
  const { user } = useAuth();
  const { studentData, updateStudent, refreshStudentData } = useStudent();

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<any>('');
  const [gender, setGender] = useState('');
  const [status, setStatus] = useState('');
  const [defaultData, setDefaultData] = useState<FormProps>();

  useEffect(() => {
    updateStudent(studentId);
  }, [updateStudent, studentId]);

  const handleObservation = (observation: string) => {
    const array = observation.split(`/${user.id}`);

    const newObservation = array.find((elem) =>
      elem.includes(`${user.id}&&!!`),
    );
    const regex = new RegExp(`${user.id}&&!!`, 'g');

    if (newObservation) {
      return newObservation.replace(regex, '');
    }

    return '';
  };

  const handleSaveObservation = (observation: string) => {
    const array = studentData?.student?.observations?.split(`/${user.id}`);
    const newData = array?.filter((elem) => !elem.includes(`${user.id}&&!!`));
    const axuArray = observation.split(`/${user.id}`);

    if (axuArray.length > 1) {
      const newObservation = `/${user.id}${handleObservation(observation)}${
        user.id
      }&&!!/${user.id}`;
      return `${newData[0]}${newObservation}`;
    }

    const newObservation = `/${user.id}${observation.replace(
      `${user.id}&&!!`,
      '',
    )}${user.id}&&!!/${user.id}`;
    if (newData) {
      return `${newData[0]}${newObservation}`;
    }
    return `${newObservation}`;
  };

  const loadStudentData = useCallback(() => {
    const profileData = {
      image_url: studentData.image_url,
      name: studentData.name,
      email: studentData.email,
      cellphone: studentData.cellphone,
      address_zip_code: studentData.address_zip_code,
      address_street: studentData.address_street,
      address_number: studentData.address_number,
      address_complement: studentData.address_complement,
      address_city: studentData.address_city,
      address_state: studentData.address_state,
      address_country: studentData.address_country,
      address_neighborhood: studentData.address_neighborhood,
      cpf: studentData.student.cpf,
      observations: studentData.student.observations,
      birth_date: format(
        addDays(new Date(studentData.student.birth_date), 1),
        'yyyy-MM-dd',
      ),
      date_registration: format(new Date(studentData.created_at), 'yyyy-MM-dd'),
    };

    formRef.current?.setFieldValue('cpf', studentData.student.cpf ?? '');
    formRef.current?.setFieldValue('cellphone', studentData.cellphone ?? '');
    formRef.current?.setFieldValue(
      'address_zip_code',
      studentData.address_zip_code ?? '',
    );
    formRef.current?.setFieldValue(
      'observations',
      handleObservation(studentData.student.observations || ''),
    );

    setStatus(studentData.student.status);
    setGender(studentData.student.genre);

    setDefaultData(profileData);

    setPreview(studentData.image_url);
  }, [studentData]);

  useEffect(() => {
    loadStudentData();
  }, [loadStudentData]);

  const radioStatusOptionsList = useMemo(
    () => [
      { id: 'active', label: 'Ativo' },
      { id: 'inactive', label: 'Inativo' },
    ],
    [],
  );

  const RadioStatusOptions = useMemo(
    () =>
      radioStatusOptionsList.map((item) => (
        <RadioInput
          key={item.label}
          isSelected={item.id === status}
          onClick={() => setStatus(item.id)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [radioStatusOptionsList, status],
  );

  const inputStyle = {
    height: 35,
    padding: '0 1.7rem',
  };

  const textAreaStyle = useMemo(
    () => ({
      width: '100%',
      marginTop: 5,

      height: '19rem',
      padding: '2rem',
      borderRadius: '1rem',
    }),
    [],
  );

  const handleSubmit: SubmitHandler<FormProps> = useCallback(
    async (data) => {
      setLoading(true);

      const formData = {
        ...data,
        date_registration: defaultData?.date_registration,
        professional_id: user.professional.id,
        image_url: preview,
        genre: gender,
        status,
        cpf: data.cpf ? data.cpf.replace(/\D/gim, '') : data.cpf,
        cellphone: data.cellphone
          ? data.cellphone.replace(/\D/gim, '')
          : data.cellphone,
        address_zip_code: data.address_zip_code
          ? data.address_zip_code.replace(/\D/gim, '')
          : data.address_zip_code,
        observations: handleSaveObservation(data.observations || ''),
      };

      const schema = Yup.object().shape({
        name: Yup.string().required('O campo é obrigatório!'),
        cpf: Yup.string().required('O campo é obrigatório!'),
        email: Yup.string()
          .required('O campo é obrigatório!')
          .email('Email inválido!'),
      });

      try {
        await schema.validate(data, { abortEarly: false });

        await api.put(`students/${studentData.id}`, formData);

        await refreshStudentData();

        setLoading(false);

        toast.success('Aluno atualizado com sucesso!');

        goBack();
      } catch (err) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          toast.error('Preencha os campos corretamente!');
        } else {
          toast.error('Ocorreu um erro ao atualizar o aluno!');
        }
      }
    },
    [
      defaultData,
      gender,
      status,
      goBack,
      studentData,
      user,
      preview,
      formRef.current,
    ],
  );

  return (
    <Layout title="MEUS ALUNOS">
      <PageHeader>
        <PageHeaderBackButton onClick={() => goBack()}>
          <IoIosArrowBack />
          <PageHeaderBackButtonLabel>editar aluno</PageHeaderBackButtonLabel>
        </PageHeaderBackButton>

        <PageHeaderSaveButton
          disabled={loading}
          onClick={() => formRef.current?.submitForm()}
        >
          Salvar
        </PageHeaderSaveButton>
      </PageHeader>

      <Container>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={defaultData}>
          <FormContainer>
            <FieldSet>
              <ImageLabel htmlFor="logo">
                <p>Foto:</p>
                <Image src={preview} alt="Imagem do usuário" />
              </ImageLabel>
            </FieldSet>

            <FieldsWrapper>
              <FieldSet>
                <FormLabel>Nome:</FormLabel>
                <Input
                  name="name"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>CPF:</FormLabel>
                <InputMask
                  name="cpf"
                  mask="999.999.999-99"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Email:</FormLabel>
                <Input
                  name="email"
                  type="email"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Cadastrado em:</FormLabel>
                <InputDate
                  name="date_registration"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Observações:</FormLabel>
                <Textarea
                  name="observations"
                  containerStyle={textAreaStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Status: *</FormLabel>
                <>{RadioStatusOptions}</>
              </FieldSet>
            </FieldsWrapper>
          </FormContainer>
        </Form>
      </Container>
    </Layout>
  );
};

export default StudentRegister;
